import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheck,
  FiPlay,
  FiFlag,
  FiCheckCircle,
  FiMapPin,
  FiCalendar,
  FiZap,
} from "react-icons/fi";
import { Radar } from "lucide-react";
import {
  getMission,
  listMyMissions,
  setMissionStatus,
  STATUS_LABELS,
  STATUS_TONES,
  typeLabel,
  formatDate,
  formatPrice,
} from "../../utils/missionsApi";
import MissionTypeIcon from "./components/MissionTypeIcon";

const STEPS = [
  { key: "ouverte",               label: "Mission publiée" },
  { key: "propositions_recues",   label: "Propositions reçues" },
  { key: "proposition_acceptee",  label: "Proposition acceptée" },
  { key: "en_cours",              label: "Travaux en cours" },
  { key: "terminee",              label: "Travaux terminés" },
  { key: "validee",               label: "Mission validée" },
];

function TimelineFor({ mission }) {
  const order = STEPS.map((s) => s.key);
  const currentIdx = order.indexOf(mission.status);
  return (
    <div className="pmm-timeline">
      {STEPS.map((s, i) => {
        const isDone = i < currentIdx;
        const isCurrent = i === currentIdx;
        return (
          <div key={s.key} className={`pmm-timeline-step ${isDone ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}>
            <div className="pmm-timeline-dot">{isDone ? <FiCheck /> : i + 1}</div>
            <div>
              <div className="pmm-timeline-step__label">{s.label}</div>
              <div className="pmm-timeline-step__sub">
                {isCurrent ? "Étape en cours" : isDone ? "Terminé" : "À venir"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MissionTracking() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState(paramId ? "single" : "list");
  const [mission, setMission] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem("userId");

  const loadSingle = useCallback(async () => {
    setLoading(true);
    try {
      const m = await getMission(paramId);
      setMission(m);
    } finally { setLoading(false); }
  }, [paramId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        listMyMissions("created").catch(() => ({ items: [] })),
        listMyMissions("assigned").catch(() => ({ items: [] })),
      ]);
      const merged = [...(a.items || []), ...(b.items || [])]
        .filter((m, idx, arr) => arr.findIndex((x) => x._id === m._id) === idx);
      setList(merged.filter((m) => !["validee", "annulee"].includes(m.status)));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (paramId) {
      setMode("single");
      loadSingle();
    } else {
      setMode("list");
      loadList();
    }
  }, [paramId, loadSingle, loadList]);

  const handleStatus = async (status) => {
    if (!mission) return;
    if (!window.confirm(`Changer le statut vers "${STATUS_LABELS[status]}" ?`)) return;
    try {
      await setMissionStatus(mission._id, status);
      loadSingle();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  if (mode === "single") {
    if (loading) return <div className="pmm-card"><div className="pmm-spinner" /></div>;
    if (!mission) return (
      <div className="pmm-empty">
        <div className="pmm-empty-icon">❓</div>
        <h3>Mission introuvable</h3>
      </div>
    );
    const isOwner = String(mission.createdBy?._id || mission.createdBy) === userId;
    const isAssigned = mission.assignedTo && String(mission.assignedTo._id || mission.assignedTo) === userId;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div className="pmm-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="pmm-btn" onClick={() => navigate("/missions/tracking")}><FiArrowLeft /> Retour</button>
            <MissionTypeIcon type={mission.type} size={48} />
            <div>
              <h1>{mission.title}</h1>
              <div className="pmm-topbar-sub">{typeLabel(mission.type)} • {mission.city}</div>
            </div>
          </div>
          <span className={`pmm-chip pmm-chip--${STATUS_TONES[mission.status] || "neutral"}`}>
            {STATUS_LABELS[mission.status] || mission.status}
          </span>
        </div>

        <div className="pmm-card">
          <TimelineFor mission={mission} />
        </div>

        <div className="pmm-card" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {isAssigned && mission.status === "proposition_acceptee" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("en_cours")}>
              <FiPlay /> Démarrer
            </button>
          )}
          {isAssigned && mission.status === "en_cours" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("terminee")}>
              <FiFlag /> Marquer terminée
            </button>
          )}
          {isOwner && mission.status === "terminee" && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => handleStatus("validee")}>
              <FiCheckCircle /> Valider le travail
            </button>
          )}
          {(isOwner || isAssigned) && (
            <>
              <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${mission._id}`)}>Voir la mission</button>
              <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${mission._id}/photos`)}>Photos / validation</button>
              <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${mission._id}/messages`)}>Messages</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // List mode
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="pmm-topbar">
        <div>
          <h1 className="pmm-h-accent">Suivi des missions</h1>
          <div className="pmm-topbar-sub">Vos missions en cours, qu'elles soient postées ou acceptées.</div>
        </div>
      </div>

      {loading ? <div className="pmm-spinner" />
        : list.length === 0 ? (
          <div className="pmm-empty">
            <div className="pmm-empty-icon"><Radar size={28} /></div>
            <h3>Rien à suivre pour le moment</h3>
            <p>Vos missions actives apparaîtront ici.</p>
          </div>
        ) : (
          <div className="pmm-grid pmm-grid--2">
            {list.map((m) => (
              <article className={`pmm-mission-card ${m.isUrgent ? "is-urgent" : ""}`} key={m._id}>
                <div className="pmm-mission-card__head">
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                    <MissionTypeIcon type={m.type} size={38} />
                    <div style={{ minWidth: 0 }}>
                      <h3 className="pmm-mission-card__title">{m.title}</h3>
                      <div className="pmm-mission-card__type">{typeLabel(m.type)}</div>
                    </div>
                  </div>
                  <span className={`pmm-chip pmm-chip--${STATUS_TONES[m.status] || "neutral"}`}>
                    {STATUS_LABELS[m.status] || m.status}
                  </span>
                </div>

                <div className="pmm-mission-card__meta">
                  <span><FiMapPin /> <b>{m.city}</b></span>
                  <span><FiCalendar /> {formatDate(m.date)}</span>
                  <span>{formatPrice(m.estimatedBudget)}</span>
                  <span><FiZap /> {m.isUrgent ? "Urgent" : "Normal"}</span>
                </div>

                <div className="pmm-mission-card__actions">
                  <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/tracking/${m._id}`)}>
                    Suivi détaillé
                  </button>
                  <button className="pmm-btn pmm-btn--primary" onClick={() => navigate(`/missions/${m._id}`)}>
                    Ouvrir
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
    </div>
  );
}
